// "Where you lost time": the deterministic list the sim computes from the log (orders issued late
// against a contact, a reserve never committed, no ISR on a named area of interest before contact, a
// fuel warning ignored, a saturated command net). Amber cards, minutes on the right, and the events
// each item is drawn from as chips that scrub the replay to the moment in question.

import { useMemo } from 'react';
import { lostTime } from '../../debrief/analyze';
import type { Event, Graphic, Order, Unit } from '../../sim/types';
import { useDebrief } from '../../state/debrief';
import { clockHM } from '../reports';
import { Heading } from './Logs';

export function LostTime({
  events, orders, graphics, units, runTicks,
}: {
  events: Event[];
  orders: Order[];
  graphics: Graphic[];
  units: Unit[];
  /** How long the run lasted, in sim ticks: the "never made contact" rule is priced in run length. */
  runTicks: number;
}) {
  const items = useMemo(
    () => lostTime(events, orders, graphics, units, runTicks),
    [events, orders, graphics, units, runTicks],
  );
  const eventById = useMemo(() => new Map(events.map((e) => [e.id, e])), [events]);
  const scrubToEvent = useDebrief((s) => s.scrubToEvent);
  const total = items.reduce((sum, i) => sum + i.minutes, 0);

  return (
    <section data-testid="lost-time" className="wt-panel p-3">
      <div className="flex items-baseline justify-between">
        <Heading>Where you lost time</Heading>
        <span className="mono text-[12px]" style={{ color: 'var(--color-warn)' }}>
          {Math.round(total)} min
        </span>
      </div>
      <p className="mt-1 text-[11px] leading-snug text-fog">
        Computed from the event log, not from an opinion: each item names the rule it tripped and the
        events it was drawn from. Click an event to scrub the replay to that minute.
      </p>

      <div className="mt-2 space-y-2">
        {items.length === 0 && (
          <p className="mono text-[11px] text-fog">
            Nothing lost: you closed with the enemy, every contact drew an order inside five minutes,
            and the net never saturated.
          </p>
        )}
        {items.map((item, i) => (
          <article
            key={`${item.item}-${i}`}
            data-testid="lost-time-item"
            className="wt-card"
            style={{ borderColor: 'color-mix(in srgb, var(--color-warn) 45%, var(--color-slate-700))', borderLeftWidth: 2 }}
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[12px] text-paper">{item.item}</span>
              <span className="mono shrink-0 text-[12px]" style={{ color: 'var(--color-warn)' }}>
                {item.minutes === 0 ? '—' : `${Math.round(item.minutes)} min`}
              </span>
            </div>
            {item.eventIds.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {item.eventIds.map((id) => {
                  const ev = eventById.get(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      data-testid="lost-time-chip"
                      onClick={() => ev && scrubToEvent(ev.t)}
                      title={ev ? 'Scrub the replay to this event' : 'Event not in the log'}
                      className="mono rounded-sm border border-slate-700 px-1.5 py-0.5 text-[10px] uppercase tracking-[0.12em] text-fog hover:text-[color:var(--color-warn)]"
                    >
                      #{id} {ev ? `${clockHM(ev.t)} ${ev.kind}` : 'unknown'}
                    </button>
                  );
                })}
              </div>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}
